import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { getDashboard, getSurgicalAudit, getQuestionCounts } from "../services/api";
import { useCert } from "./CertContext";
import { useAuth0 } from "@auth0/auth0-react";

const AppDataContext = createContext(null);
const RETRY_DELAYS_MS = [0, 1500, 2000];

const wait = (milliseconds) => new Promise(resolve => setTimeout(resolve, milliseconds));

export function AppDataProvider({ children }) {
    const {
        activeCert,
        loading: certLoading,
        catalogReady,
        configError,
        _setExamDateRaw,
        setIsChangingCert,
    } = useCert();
    const { isAuthenticated, isLoading: authLoading, user } = useAuth0();

    const [store, setStore] = useState({
        dashboardData: null,
        auditData: null,
        questionCounts: {},
        // Compatibility field for older consumers. Authenticated learners
        // always have access to the complete product.
        isPurchased: true,
        needsOnboarding: false,
    });
    const [initialLoad, setInitialLoad] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [auditError, setAuditError] = useState(null);
    const [sharedDisplayName, setSharedDisplayName] = useState(
        () => localStorage.getItem("displayName") || ""
    );
    // One monotonically increasing generation covers initial loads, manual
    // refreshes and Dashboard refreshes. A retry keeps the generation it
    // started with, so a delayed A retry can never supersede a later B load.
    const fetchIdRef = useRef(0);
    // Paired refreshes can share a separately requested recovery audit.
    const auditFetchIdRef = useRef(0);
    const auditRequestRef = useRef(null);
    const snapshotRequestRef = useRef(null);
    const selectionRef = useRef(null);
    selectionRef.current = { cert: activeCert, userId: user?.sub,
        ready: isAuthenticated && !authLoading && !certLoading && catalogReady };
    const matchesSelection = useCallback((cert, userId) => (
        selectionRef.current.ready
        && selectionRef.current.cert === cert
        && selectionRef.current.userId === userId
    ), []);

    const applyDashboardSideEffects = useCallback((dashboard) => {
        if (_setExamDateRaw) _setExamDateRaw(dashboard.examDate || null);
        if (dashboard.displayName) {
            localStorage.setItem("displayName", dashboard.displayName);
            setSharedDisplayName(dashboard.displayName);
        }
        localStorage.setItem("streakCount", dashboard.streak || 0);
        const todayISO = new Date().toISOString().split("T")[0];
        localStorage.setItem("todayCount", dashboard.dailyActivity?.[todayISO] || 0);
        window.dispatchEvent(new Event("storage"));
    }, [_setExamDateRaw]);

    const updateDisplayName = useCallback((name) => {
        const nextName = String(name || "").trim();
        if (!nextName) return;
        localStorage.setItem("displayName", nextName);
        setSharedDisplayName(nextName);
        setStore(prev => ({
            ...prev,
            dashboardData: prev.dashboardData
                ? { ...prev.dashboardData, displayName: nextName }
                : prev.dashboardData,
        }));
    }, []);

    const fetchAll = useCallback(async (
        cert,
        userId,
        { silent = false, includeAnalytics = true, reuseAudit = false, requestId = ++fetchIdRef.current } = {}
    ) => {
        if (!userId || !cert) {
            if (requestId === fetchIdRef.current) {
                setInitialLoad(false);
                setIsChangingCert(false);
            }
            return { ok: false, reason: "missing_selection" };
        }

        const pendingAudit = auditRequestRef.current;
        const sharedAudit = includeAnalytics && reuseAudit && pendingAudit?.cert === cert
            && pendingAudit.userId === userId && pendingAudit.id === auditFetchIdRef.current
            ? pendingAudit : null;
        const auditRequestId = includeAnalytics
            ? (sharedAudit?.id ?? ++auditFetchIdRef.current) : null;
        const ownsAudit = () => includeAnalytics
            && auditRequestId === auditFetchIdRef.current
            && matchesSelection(cert, userId);

        if (!silent) {
            setInitialLoad(true);
            if (ownsAudit()) setAuditError(null);
        }
        setLoadError(null);
        let bestDashboard = null;
        let bestAudit = null;
        let bestCounts = null;
        let lastDashboardError = null;
        let lastAuditError = null;
        let dashboardPending = true;
        let auditPending = includeAnalytics;
        let countsPending = true;
        let needsOnboarding = false;

        for (let attempt = 0; attempt < RETRY_DELAYS_MS.length; attempt += 1) {
            if (attempt > 0) {
                await wait(RETRY_DELAYS_MS[attempt]);
                if (requestId !== fetchIdRef.current) return { ok: false, stale: true };
            }

            const [dashRes, auditRes, countRes] = await Promise.allSettled([
                dashboardPending ? getDashboard(userId, cert) : Promise.resolve(null),
                auditPending ? (sharedAudit
                    ? sharedAudit.promise.then(result => {
                        if (!result.ok) throw result.error || new Error("Analytics request superseded");
                        return result.auditData;
                    })
                    : getSurgicalAudit(cert, userId)) : Promise.resolve(null),
                countsPending ? getQuestionCounts() : Promise.resolve(null),
            ]);

            if (requestId !== fetchIdRef.current) return { ok: false, stale: true };

            if (dashboardPending) {
                if (dashRes.status === "fulfilled" && dashRes.value) {
                    bestDashboard = dashRes.value;
                    dashboardPending = false;
                } else {
                    lastDashboardError = dashRes.status === "rejected"
                        ? dashRes.reason
                        : new Error("Empty dashboard response");
                    if (lastDashboardError?.response?.status === 404) {
                        dashboardPending = false;
                        needsOnboarding = true;
                    }
                }
            }

            if (auditPending) {
                if (auditRes.status === "fulfilled" && auditRes.value) {
                    bestAudit = auditRes.value;
                    auditPending = false;
                } else {
                    lastAuditError = auditRes.status === "rejected"
                        ? auditRes.reason
                        : new Error("Empty analytics response");
                }
            }

            if (countsPending && countRes.status === "fulfilled") {
                bestCounts = countRes.value || {};
                countsPending = false;
            }

            if (needsOnboarding || (!dashboardPending && !auditPending && !countsPending)) break;
            if (process.env.NODE_ENV !== "production" && attempt < RETRY_DELAYS_MS.length - 1) {
                console.warn(`Application data fetch incomplete. Auto-retry ${attempt + 1}...`);
            }
        }

        if (requestId !== fetchIdRef.current) return { ok: false, stale: true };

        if (bestDashboard) {
            applyDashboardSideEffects(bestDashboard);
            setStore(prev => ({
                ...prev,
                dashboardData: bestDashboard,
                auditData: ownsAudit() ? (bestAudit ?? prev.auditData) : prev.auditData,
                questionCounts: bestCounts ?? prev.questionCounts,
                isPurchased: true,
                needsOnboarding: false,
            }));
            if (ownsAudit()) {
                setAuditError(bestAudit ? null : (lastAuditError || new Error("Could not load analytics")));
            }
            // The newest request owns completion of bootstrap too. A silent
            // refresh may have superseded the request that showed the loader.
            setInitialLoad(false);
            setIsChangingCert(false);
            return {
                ok: true,
                partial: (includeAnalytics && !bestAudit) || bestCounts === null,
                auditError: bestAudit ? null : lastAuditError,
            };
        }

        if (needsOnboarding) {
            setStore(prev => ({
                ...prev,
                dashboardData: null,
                auditData: ownsAudit() ? (bestAudit ?? prev.auditData) : prev.auditData,
                questionCounts: bestCounts ?? prev.questionCounts,
                isPurchased: true,
                needsOnboarding: true,
            }));
            if (ownsAudit()) setAuditError(bestAudit ? null : lastAuditError);
            setInitialLoad(false);
            setIsChangingCert(false);
            return { ok: false, reason: "onboarding" };
        }

        // Preserve any independently successful analytics/count response, but
        // surface the dashboard failure rather than leaving the global loader
        // running forever.
        setStore(prev => silent ? ({
            ...prev,
            auditData: ownsAudit() ? (bestAudit ?? prev.auditData) : prev.auditData,
            questionCounts: bestCounts ?? prev.questionCounts,
        }) : ({
            ...prev,
            dashboardData: null,
            auditData: ownsAudit() ? bestAudit : prev.auditData,
            questionCounts: bestCounts ?? prev.questionCounts,
            isPurchased: true,
            needsOnboarding: false,
        }));
        if (ownsAudit()) setAuditError(bestAudit ? null : lastAuditError);
        setLoadError(lastDashboardError || new Error("Could not load application data"));
        setInitialLoad(false);
        setIsChangingCert(false);
        return { ok: false, error: lastDashboardError };
    }, [applyDashboardSideEffects, setIsChangingCert, matchesSelection]);

    const requestSnapshot = useCallback((cert, userId, options) => {
        const promise = fetchAll(cert, userId, options);
        const pending = { cert, userId, id: options.requestId, promise };
        snapshotRequestRef.current = pending;
        const clearPending = () => {
            if (snapshotRequestRef.current === pending) snapshotRequestRef.current = null;
        };
        promise.then(clearPending, clearPending);
        return promise;
    }, [fetchAll]);

    useEffect(() => {
        if (authLoading || certLoading) return undefined;

        const requestId = ++fetchIdRef.current;
        auditFetchIdRef.current += 1;
        auditRequestRef.current = null;
        if (!catalogReady) {
            setStore(prev => ({
                ...prev,
                dashboardData: null,
                auditData: null,
                isPurchased: true,
                needsOnboarding: false,
            }));
            setLoadError(configError || new Error("Certification catalogue unavailable"));
            setAuditError(null);
            setInitialLoad(false);
            setIsChangingCert(false);
        } else if (isAuthenticated && user?.sub && activeCert) {
            setStore(prev => ({
                ...prev,
                dashboardData: null,
                auditData: null,
                isPurchased: true,
                needsOnboarding: false,
            }));
            // Prepare Analytics with the dashboard so opening the route can
            // render the audit immediately from this shared snapshot.
            requestSnapshot(activeCert, user.sub, { silent: false, requestId });
        } else {
            // Auth is settled and there is nothing fetchable. This is a valid
            // terminal bootstrap state (signed out or no active inventory), not
            // a reason to strand the entire application behind a spinner.
            setInitialLoad(false);
            setIsChangingCert(false);
            setAuditError(null);
        }

        return () => {
            if (fetchIdRef.current === requestId) fetchIdRef.current += 1;
            auditFetchIdRef.current += 1;
            auditRequestRef.current = null;
            snapshotRequestRef.current = null;
        };
    }, [activeCert, catalogReady, configError, isAuthenticated, authLoading, certLoading, user?.sub, requestSnapshot, setIsChangingCert]);

    // Dashboard refreshes prepare Analytics too. Entry/focus can share a pending
    // snapshot; mutations use the default to fetch evidence after their changes.
    const refreshDashboard = useCallback((opts = {}) => {
        const userId = user?.sub;
        if (!userId || !activeCert) return Promise.resolve({ ok: false });
        if (!matchesSelection(activeCert, userId)) return Promise.resolve({ ok: false, stale: true });
        const pending = snapshotRequestRef.current;
        if (opts.reusePending && pending?.cert === activeCert && pending.userId === userId
            && pending.id === fetchIdRef.current) return pending.promise;
        const requestId = ++fetchIdRef.current;
        return requestSnapshot(activeCert, userId, {
            silent: true, reuseAudit: Boolean(opts.reusePending), requestId,
        });
    }, [activeCert, user?.sub, requestSnapshot, matchesSelection]);

    const refreshAll = useCallback((opts = {}) => {
        const silent = opts.silent !== false;
        if (!activeCert || !user?.sub) return Promise.resolve({ ok: false });
        if (!matchesSelection(activeCert, user.sub)) return Promise.resolve({ ok: false, stale: true });
        const requestId = ++fetchIdRef.current;
        return requestSnapshot(activeCert, user.sub, { silent, requestId });
    }, [activeCert, user?.sub, requestSnapshot, matchesSelection]);

    // The audit response contains the complete domain/subdomain/topic tree.
    // Fetch it independently so unrelated dashboard/count requests cannot
    // delay the first Analytics view or invalidate its result.
    const refreshAnalytics = useCallback(() => {
        const cert = activeCert;
        const userId = user?.sub;
        if (!matchesSelection(cert, userId)) return Promise.resolve({ ok: false, stale: true });

        const pending = auditRequestRef.current;
        if (pending?.cert === cert && pending.userId === userId
            && pending.id === auditFetchIdRef.current) return pending.promise;

        const id = ++auditFetchIdRef.current;
        const isCurrent = () => id === auditFetchIdRef.current && matchesSelection(cert, userId);
        setAuditError(null);
        const promise = (async () => {
            let lastError;
            for (let attempt = 0; attempt < RETRY_DELAYS_MS.length; attempt += 1) {
                if (attempt > 0) await wait(RETRY_DELAYS_MS[attempt]);
                if (!isCurrent()) return { ok: false, stale: true };
                try {
                    const audit = await getSurgicalAudit(cert, userId);
                    if (!isCurrent()) return { ok: false, stale: true };
                    if (!audit) throw new Error("Empty analytics response");
                    setStore(prev => ({ ...prev, auditData: audit }));
                    setAuditError(null);
                    return { ok: true, auditData: audit };
                } catch (err) {
                    if (!isCurrent()) return { ok: false, stale: true };
                    lastError = err;
                }
            }
            setAuditError(lastError);
            return { ok: false, error: lastError };
        })();
        auditRequestRef.current = { cert, userId, id, promise };
        const clearPending = () => {
            if (auditRequestRef.current?.id === id) auditRequestRef.current = null;
        };
        promise.then(clearPending, clearPending);
        return promise;
    }, [activeCert, user?.sub, matchesSelection]);

    return (
        <AppDataContext.Provider value={{
            dashboardData: store.dashboardData,
            auditData: store.auditData,
            questionCounts: store.questionCounts,
            isPurchased: store.isPurchased,
            needsOnboarding: store.needsOnboarding,
            displayName: sharedDisplayName,
            loading: initialLoad,
            loadError,
            auditError,
            dataStale: Boolean(loadError && store.dashboardData),
            analyticsDataStale: Boolean(auditError && store.auditData),
            updateDisplayName,
            refreshDashboard,
            refreshAll,
            refreshAnalytics,
        }}>
            {children}
        </AppDataContext.Provider>
    );
}

export function useAppData() {
    const ctx = useContext(AppDataContext);
    if (!ctx) throw new Error("useAppData must be used inside AppDataProvider");
    return ctx;
}
